// frontend/src/features/campaign/components/list/BlueprintPopover.js
import React, { useRef, useEffect, useState } from 'react';
import axios from '../../../../api/axios';

import { INSPIRATION_PROMPTS, getBlueprintTemplateByPrompt } from '../../data/blueprintTemplates';

export default function BlueprintPopover({ isOpen, onToggle, onClose, onSelectPlaybook }) {
  const popoverRef = useRef(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedBlueprint, setGeneratedBlueprint] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleGenerateBlueprint = async (promptText) => {
    const textToUse = (promptText || customPrompt || '').trim();
    if (!textToUse) return;

    // 1. Instant template check
    const template = getBlueprintTemplateByPrompt(textToUse);
    if (template) {
      setErrorMsg('');
      setGeneratedBlueprint(template);
      return;
    }

    // 2. Custom prompt fallback
    try {
      setIsGenerating(true);
      setErrorMsg('');
      const res = await axios.post('outreach/generate-blueprint', {
        prompt: textToUse,
      });

      if (res.data?.success && res.data?.data) {
        setGeneratedBlueprint(res.data.data);
      } else {
        setErrorMsg('Failed to generate blueprint. Please try again.');
      }
    } catch (err) {
      console.error('[BlueprintPopover] Generation error:', err);
      setErrorMsg(err.response?.data?.error || err.message || 'AI generation failed');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="cmp-blueprint-dropdown-wrap" ref={popoverRef}>
      <button
        type="button"
        className={`cmp-blueprint-icon-btn cmp-blueprint-ai ${isOpen ? 'active' : ''}`}
        onClick={onToggle}
        title="AI Real-Time Strategy Blueprints"
        aria-label="AI Real-Time Strategy Blueprints"
      >
        <span className="cmp-ai-bolt-badge">
          <i className="fas fa-bolt" />
        </span>
        <span className="cmp-blueprint-btn-label">AI Blueprints</span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} cmp-blueprint-caret`} />
      </button>

      {/* Dropdown Popover Content */}
      {isOpen && (
        <div className="cmp-blueprint-popover-menu">
          {/* Header */}
          <div className="cmp-presets-header">
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                flexWrap: 'wrap',
                gap: '8px',
              }}
            >
              <span className="cmp-presets-title">
                <i className="fas fa-magic" style={{ color: '#6366f1' }} /> Real-Time AI Blueprint
                Studio
              </span>
              <span className="cmp-ai-powered-badge">
                <i className="fas fa-brain" /> Powered by AI Engine
              </span>
            </div>
            <span className="cmp-presets-subtitle">
              Describe your product, target audience, or campaign angle to synthesize a complete
              6-step B2B outreach strategy in real time:
            </span>
          </div>

          {/* Real-Time Generator Bar */}
          <div className="cmp-blueprint-ai-gen-bar">
            <div className="cmp-blueprint-input-wrap">
              <i className="fas fa-sparkles cmp-blueprint-input-icon" />
              <input
                type="text"
                className="cmp-blueprint-gen-input"
                placeholder="e.g. Enterprise CyberSecurity for Banking CISOs in Malaysia & SG..."
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleGenerateBlueprint();
                }}
                disabled={isGenerating}
              />
            </div>
            <button
              type="button"
              className="cmp-btn-primary cmp-blueprint-gen-submit-btn"
              onClick={() => handleGenerateBlueprint()}
              disabled={isGenerating || !customPrompt.trim()}
            >
              {isGenerating ? (
                <>
                  <i className="fas fa-spinner fa-spin" /> Generating...
                </>
              ) : (
                <>
                  <i className="fas fa-bolt" /> Generate Blueprint
                </>
              )}
            </button>
          </div>

          {/* Quick Inspiration / Template Pills */}
          <div className="cmp-blueprint-inspo-row">
            <span className="cmp-inspo-label">Campaign Templates:</span>
            <div className="cmp-inspo-chips">
              {INSPIRATION_PROMPTS.map((prompt, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="cmp-inspo-chip"
                  disabled={isGenerating}
                  onClick={() => {
                    setCustomPrompt(prompt);
                    handleGenerateBlueprint(prompt);
                  }}
                >
                  <i className="fas fa-layer-group" /> {prompt}
                </button>
              ))}
            </div>
          </div>

          {/* Generation Loading State */}
          {isGenerating && (
            <div className="cmp-blueprint-loading-card">
              <div className="cmp-ai-pulse-spinner">
                <i className="fas fa-brain fa-spin" />
              </div>
              <div>
                <strong style={{ color: '#1e293b', fontSize: '13.5px', display: 'block' }}>
                  AI Engine is drafting your campaign strategy...
                </strong>
                <span style={{ color: '#64748b', fontSize: '12px' }}>
                  Synthesizing targeting cohorts, persona messaging matrices, and multi-channel
                  cadence...
                </span>
              </div>
            </div>
          )}

          {errorMsg && (
            <div className="cmp-blueprint-error-box">
              <i className="fas fa-exclamation-triangle" /> {errorMsg}
            </div>
          )}

          {/* Real-time Generated or Instant Template Result */}
          {generatedBlueprint && !isGenerating && (
            <div className="cmp-generated-blueprint-highlight">
              <div className="cmp-gen-blueprint-top">
                <span
                  className={`cmp-gen-badge ${generatedBlueprint.isTemplate ? 'template' : ''}`}
                >
                  <i
                    className={
                      generatedBlueprint.isTemplate ? 'fas fa-layer-group' : 'fas fa-check-circle'
                    }
                  />{' '}
                  {generatedBlueprint.isTemplate
                    ? generatedBlueprint.label
                      ? `Template: ${generatedBlueprint.label}`
                      : 'Campaign Template'
                    : 'AI Real-Time Strategy'}
                </span>
                <div className="cmp-gen-meta-wrap">
                  <span className="cmp-gen-meta-pill budget">
                    <i className="fas fa-coins" />{' '}
                    {generatedBlueprint.allocatedBudget || '$ 25,000'}
                  </span>
                  <span className="cmp-gen-meta-pill duration">
                    <i className="fas fa-clock" /> {generatedBlueprint.durationDays || 45} Days
                  </span>
                </div>
              </div>

              <h4 className="cmp-gen-title">{generatedBlueprint.name}</h4>

              <div className="cmp-gen-objective-box">
                <i className="fas fa-bullseye" />
                <span>{generatedBlueprint.primaryObjective}</span>
              </div>

              <div className="cmp-gen-details-grid">
                <div className="cmp-gen-detail-item">
                  <span className="cmp-detail-lbl">Target Decision Makers:</span>
                  <div className="cmp-persona-tags-wrap">
                    {(generatedBlueprint.targetPersonas || []).slice(0, 3).map((p, i) => (
                      <span key={i} className="cmp-persona-mini-tag">
                        <i className="fas fa-user-tie" /> {p}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="cmp-gen-detail-item">
                  <span className="cmp-detail-lbl">Cadence Channels:</span>
                  <div className="cmp-channels-tags-wrap">
                    {(generatedBlueprint.channels || ['Email', 'LinkedIn']).map((ch, i) => (
                      <span key={i} className="cmp-channel-mini-tag">
                        <i
                          className={
                            ch.toLowerCase().includes('email')
                              ? 'fas fa-envelope'
                              : ch.toLowerCase().includes('linkedin')
                                ? 'fab fa-linkedin'
                                : ch.toLowerCase().includes('phone')
                                  ? 'fas fa-phone-alt'
                                  : 'fas fa-paper-plane'
                          }
                        />
                        {ch}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="cmp-gen-action-row">
                <button
                  type="button"
                  className="cmp-btn-use-generated"
                  onClick={() => onSelectPlaybook(generatedBlueprint)}
                >
                  <i className="fas fa-magic" /> Open Strategy in Campaign Wizard{' '}
                  <i className="fas fa-arrow-right" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
